import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListStudentsQueryKey,
  useListStudents,
  useCreateStudent,
  useImportStudents,
  useUpdateStudent,
  useUpdateGuardian,
  type Student,
  type StudentImportResult,
  type CheckoutType
} from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { UsersIcon, PlusIcon, PencilIcon, UploadIcon, CheckCircle2Icon, AlertTriangleIcon, XCircleIcon, FileSpreadsheetIcon } from 'lucide-react';

const DEFAULT_APPROVAL_OPTIONS: { value: CheckoutType; label: string }[] = [
  { value: 'by_self', label: 'Go home independently' },
  { value: 'wait_for_parent', label: 'Wait for parent pickup' },
  { value: 'with_person', label: 'Leave with named person' },
  { value: 'with_teacher', label: 'Leave with teacher' },
  { value: 'back_to_school', label: 'Return to school' }
];

function defaultApprovalLabel(type: CheckoutType | null | undefined): string {
  return DEFAULT_APPROVAL_OPTIONS.find((o) => o.value === type)?.label ?? 'None';
}

export function StudentsPage() {
  const { data, isLoading } = useListStudents();
  const students = data?.students ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Students"
        description="Student roster with linked guardians for SMS preference matching"
        icon={UsersIcon}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <ImportStudentsDialog />
            <CreateStudentDialog />
          </div>
        )}
      />
      <Card className="overflow-hidden border-border/80 bg-card py-0 shadow-sm shadow-primary/5">
        <Table className="[&_thead]:bg-muted/70 [&_thead_th]:h-12 [&_thead_th]:px-4 [&_thead_th]:text-xs [&_thead_th]:font-semibold [&_thead_th]:uppercase [&_thead_th]:tracking-wider [&_tbody_td]:px-4 [&_tbody_td]:py-4">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Guardian</TableHead>
              <TableHead>Guardian Phone</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Default Approval</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                  Loading students…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && students.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                  No students registered yet.
                </TableCell>
              </TableRow>
            )}
            {students.map((s: Student) => (
              <StudentRow key={s.id} student={s} />
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function StudentRow({ student }: { student: Student }) {
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = React.useState(false);

  const studentMutation = useUpdateStudent();
  const guardianMutation = useUpdateGuardian();

  const [form, setForm] = React.useState({
    firstName: student.firstName,
    lastInitial: student.lastInitial,
    group: student.group,
    status: student.status,
    defaultCheckoutType: student.defaultCheckoutType ?? '__none__',
    defaultAuthorizedPersonName: student.defaultAuthorizedPersonName ?? ''
  });

  // Guardian form (first guardian if linked)
  const guardian = student.guardians?.[0];
  const [guardianForm, setGuardianForm] = React.useState({
    name: guardian?.name ?? '',
    phoneNumber: guardian?.phoneNumber ?? ''
  });

  React.useEffect(() => {
    if (editOpen) {
      setForm({
        firstName: student.firstName,
        lastInitial: student.lastInitial,
        group: student.group,
        status: student.status,
        defaultCheckoutType: student.defaultCheckoutType ?? '__none__',
        defaultAuthorizedPersonName: student.defaultAuthorizedPersonName ?? ''
      });
      const g = student.guardians?.[0];
      setGuardianForm({ name: g?.name ?? '', phoneNumber: g?.phoneNumber ?? '' });
    }
  }, [editOpen, student]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Update student
    await studentMutation.mutateAsync({
      id: student.id,
      data: {
        firstName: form.firstName,
        lastInitial: form.lastInitial,
        group: form.group,
        status: form.status,
        defaultCheckoutType: form.defaultCheckoutType === '__none__' ? null : form.defaultCheckoutType as CheckoutType,
        defaultAuthorizedPersonName: form.defaultCheckoutType === 'with_person' ? (form.defaultAuthorizedPersonName || null) : null
      }
    });
    // Update guardian if linked and changed
    if (guardian && (guardianForm.name !== guardian.name || guardianForm.phoneNumber !== guardian.phoneNumber)) {
      const patch: { name?: string; phoneNumber?: string } = {};
      if (guardianForm.name !== guardian.name) patch.name = guardianForm.name;
      if (guardianForm.phoneNumber !== guardian.phoneNumber) patch.phoneNumber = guardianForm.phoneNumber;
      await guardianMutation.mutateAsync({ id: guardian.id, data: patch });
    }
    await queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
    setEditOpen(false);
  };

  const isPending = studentMutation.isPending || guardianMutation.isPending;

  return (
    <>
      <TableRow className="group hover:bg-primary/5 focus-within:bg-primary/5">
        <TableCell className="font-semibold">
          {student.firstName} {student.lastInitial}.
        </TableCell>
        <TableCell>
          <Badge className="border-info/20 bg-info/10 text-info">{student.group}</Badge>
        </TableCell>
        <TableCell className="text-muted-foreground">
          {student.guardians?.map((g) => g.name).join(', ') || '—'}
        </TableCell>
        <TableCell className="font-mono text-sm text-muted-foreground">
          {student.guardians?.map((g) => g.phoneNumber).join(', ') || '—'}
        </TableCell>
        <TableCell>
          <Badge
            className={
              student.status === 'active'
                ? 'border-success/20 bg-success/10 text-success'
                : 'border-border bg-muted text-muted-foreground'
            }
          >
            {student.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className="border-border/60 text-muted-foreground">
            {defaultApprovalLabel(student.defaultCheckoutType)}
          </Badge>
          {student.defaultAuthorizedPersonName && (
            <span className="ml-1 text-xs text-muted-foreground">{student.defaultAuthorizedPersonName}</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          <Button
            variant="outline"
            size="sm"
            className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
            onClick={() => setEditOpen(true)}
          >
            <PencilIcon className="size-3.5" />
            Edit
          </Button>
        </TableCell>
      </TableRow>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Edit Student</DialogTitle>
              <DialogDescription>
                Update student details and guardian contact information.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="e-first">First Name</Label>
                  <Input id="e-first" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="e-last">Last Initial</Label>
                  <Input id="e-last" maxLength={3} placeholder="S" value={form.lastInitial} onChange={(e) => setForm({ ...form, lastInitial: e.target.value })} required />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="e-group">Group</Label>
                  <Input id="e-group" placeholder="Group A" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} required />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="e-status">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as 'active' | 'inactive' })}>
                    <SelectTrigger id="e-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-t pt-4">
                <p className="text-muted-foreground mb-3 text-sm font-medium">Default departure approval</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="e-default-checkout">Default mode</Label>
                    <Select value={form.defaultCheckoutType} onValueChange={(v) => setForm({ ...form, defaultCheckoutType: v })}>
                      <SelectTrigger id="e-default-checkout">
                        <SelectValue placeholder="No default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {DEFAULT_APPROVAL_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.defaultCheckoutType === 'with_person' && (
                    <div className="grid gap-2">
                      <Label htmlFor="e-default-person">Authorized person name</Label>
                      <Input id="e-default-person" value={form.defaultAuthorizedPersonName} onChange={(e) => setForm({ ...form, defaultAuthorizedPersonName: e.target.value })} placeholder="e.g. Grandma Sue" />
                    </div>
                  )}
                </div>
              </div>
              {guardian && (
                <div className="border-t pt-4">
                  <p className="text-muted-foreground mb-3 text-sm font-medium">Guardian (for SMS matching)</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="e-gname">Guardian Name</Label>
                      <Input id="e-gname" value={guardianForm.name} onChange={(e) => setGuardianForm({ ...guardianForm, name: e.target.value })} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="e-gphone">Guardian Phone</Label>
                      <Input id="e-gphone" placeholder="+61412345678" value={guardianForm.phoneNumber} onChange={(e) => setGuardianForm({ ...guardianForm, phoneNumber: e.target.value })} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ImportStudentsDialog() {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const mutation = useImportStudents();
  const [file, setFile] = React.useState<File | null>(null);
  const [result, setResult] = React.useState<StudentImportResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    setError(null);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setError(null);
    setResult(null);
    try {
      const res = await mutation.mutateAsync({ data: { file } });
      setResult(res);
      await queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    }
  };

  const handleClose = (next: boolean) => {
    setOpen(next);
    if (!next) setTimeout(reset, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <UploadIcon className="mr-2 size-4" />
          Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        {!result ? (
          <form onSubmit={handleUpload}>
            <DialogHeader>
              <DialogTitle>Import Students from Spreadsheet</DialogTitle>
              <DialogDescription>
                Upload a CSV file to bulk-add students. Duplicates (same first name + last initial) are skipped automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="mb-4 rounded-lg border border-dashed border-border bg-muted/40 p-4 text-sm">
                <p className="mb-2 font-medium text-foreground">Required CSV columns:</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li><code className="text-foreground">firstName</code> — student's first name</li>
                  <li><code className="text-foreground">lastInitial</code> — last initial (e.g. S)</li>
                  <li><code className="text-foreground">group</code> — group label (e.g. Group A)</li>
                  <li><code className="text-foreground">guardianName</code> — optional</li>
                  <li><code className="text-foreground">guardianPhone</code> — optional, E.164 format</li>
                </ul>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="csv-file">CSV File</Label>
                <Input
                  ref={inputRef}
                  id="csv-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                  required
                />
              </div>
              {error && (
                <p className="mt-3 text-sm text-destructive">{error}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="submit" disabled={!file || mutation.isPending}>
                {mutation.isPending ? 'Importing…' : 'Upload & Import'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div>
            <DialogHeader>
              <DialogTitle>Import Complete</DialogTitle>
              <DialogDescription>
                {result.created} student{result.created === 1 ? '' : 's'} created, {result.duplicates} duplicate{result.duplicates === 1 ? '' : 's'} skipped.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="mb-4 grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-success/20 bg-success/10 p-3 text-center">
                  <CheckCircle2Icon className="mx-auto mb-1 size-5 text-success" />
                  <p className="text-2xl font-bold text-success">{result.created}</p>
                  <p className="text-xs text-muted-foreground">Created</p>
                </div>
                <div className="rounded-lg border border-info/20 bg-info/10 p-3 text-center">
                  <AlertTriangleIcon className="mx-auto mb-1 size-5 text-info" />
                  <p className="text-2xl font-bold text-info">{result.duplicates}</p>
                  <p className="text-xs text-muted-foreground">Duplicates</p>
                </div>
                <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-center">
                  <XCircleIcon className="mx-auto mb-1 size-5 text-destructive" />
                  <p className="text-2xl font-bold text-destructive">{result.errors}</p>
                  <p className="text-xs text-muted-foreground">Errors</p>
                </div>
              </div>
              {result.details.length > 0 && (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
                  <Table className="[&_thead_th]:px-3 [&_thead_th]:py-2 [&_tbody_td]:px-3 [&_tbody_td]:py-2">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-12">Row</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Group</TableHead>
                        <TableHead className="w-24">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.details.map((d, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-muted-foreground">{d.row}</TableCell>
                          <TableCell className="font-medium">{d.firstName} {d.lastInitial}.</TableCell>
                          <TableCell className="text-muted-foreground">{d.group}</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                d.status === 'created'
                                  ? 'border-success/20 bg-success/10 text-success'
                                  : d.status === 'duplicate'
                                    ? 'border-info/20 bg-info/10 text-info'
                                    : 'border-destructive/20 bg-destructive/10 text-destructive'
                              }
                              title={d.message}
                            >
                              {d.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <FileSpreadsheetIcon className="size-4" />
                {result.total} row{result.total === 1 ? '' : 's'} processed
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => handleClose(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateStudentDialog() {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const mutation = useCreateStudent();

  const [form, setForm] = React.useState({
    firstName: '',
    lastInitial: '',
    group: '',
    guardianName: '',
    guardianPhone: '',
    defaultCheckoutType: '__none__',
    defaultAuthorizedPersonName: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await mutation.mutateAsync({
      data: {
        firstName: form.firstName,
        lastInitial: form.lastInitial,
        group: form.group,
        guardianName: form.guardianName || undefined,
        guardianPhone: form.guardianPhone || undefined,
        defaultCheckoutType: form.defaultCheckoutType === '__none__' ? null : form.defaultCheckoutType as CheckoutType,
        defaultAuthorizedPersonName: form.defaultCheckoutType === 'with_person' ? (form.defaultAuthorizedPersonName || null) : null
      }
    });
    await queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
    setForm({ firstName: '', lastInitial: '', group: '', guardianName: '', guardianPhone: '', defaultCheckoutType: '__none__', defaultAuthorizedPersonName: '' });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2 size-4" />
          Add Student
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New Student</DialogTitle>
            <DialogDescription>
              Add a student and optionally link a guardian for SMS matching.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="s-first">First Name</Label>
                <Input id="s-first" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="s-last">Last Initial</Label>
                <Input id="s-last" maxLength={3} placeholder="S" value={form.lastInitial} onChange={(e) => setForm({ ...form, lastInitial: e.target.value })} required />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="s-group">Group</Label>
              <Input id="s-group" placeholder="Group A" value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} required />
            </div>
            <div className="border-t pt-4">
              <p className="text-muted-foreground mb-3 text-sm font-medium">Default departure approval</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="s-default-checkout">Default mode</Label>
                  <Select value={form.defaultCheckoutType} onValueChange={(v) => setForm({ ...form, defaultCheckoutType: v })}>
                    <SelectTrigger id="s-default-checkout">
                      <SelectValue placeholder="No default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {DEFAULT_APPROVAL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.defaultCheckoutType === 'with_person' && (
                  <div className="grid gap-2">
                    <Label htmlFor="s-default-person">Authorized person name</Label>
                    <Input id="s-default-person" value={form.defaultAuthorizedPersonName} onChange={(e) => setForm({ ...form, defaultAuthorizedPersonName: e.target.value })} placeholder="e.g. Grandma Sue" />
                  </div>
                )}
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-muted-foreground mb-3 text-sm font-medium">Guardian (for SMS matching)</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="s-gname">Guardian Name</Label>
                  <Input id="s-gname" value={form.guardianName} onChange={(e) => setForm({ ...form, guardianName: e.target.value })} />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="s-gphone">Guardian Phone</Label>
                  <Input id="s-gphone" placeholder="+61412345678" value={form.guardianPhone} onChange={(e) => setForm({ ...form, guardianPhone: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save Student'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
